import * as electricityContractationActions from "../actions/electricityContractation";
import FormInstallation from "../components/FormInstallation";
import FormPerson from "../components/FormPerson";
import FormContract from "../components/FormContract/FormContract";
import FormGasContract from "../components/FormGasContract/dual/FormGasContract";
import FormPayment from "../components/FormPayment";
import FormConfirmation from "../components/FormConfirmation";
import Finish from "../components/Finish";
import * as collectFormDataForAPI from "../utils/collectDataAPI";
import { default as leadsService } from "../services/leads";
import { Step, StepChange } from "../types/steps";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import {
  ElecStepNames,
  StepConfirmationFormData,
  StepInstallationFormData,
  StepPersonFormData,
} from "../types/stepsFormData";
import { StepPersonFormDataAPI } from "../types/stepsAPI";
import { isEqual } from "lodash";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { useState } from "react";
import electricityContractationInitialState from "../reducers/electricityContractationInitialState";
import useContractation from "./useContractation";

interface IuseStepsProps {
  dualContractation: boolean
}

const useSteps = ({
  dualContractation
}: IuseStepsProps) => {
  const Settings = useSettingsContext();
  const dispatch = useAppDispatch();
  const { personFirstFlow } = useContractation();

  const elecContractation = useAppSelector(state => state.electricityContractation);

  const sendTrackedLeadStepData = (
    data: Parameters<typeof leadsService.sendLeadStepData>[0]
  ) => {
    if (personFirstFlow && elecContractation.leadId) {
      return leadsService.sendLeadStepData(data, elecContractation.leadId);
    }
    return leadsService.sendLeadStepData(data);
  }

  const [skippedSteps, setSkippedSteps] = useState(new Set<ElecStepNames>());

  const [resendLeadType, setResendLeadType] = useState(false);

  const findPreviousEnabledStepIdx = (currentIndex: number) => {
    const prev = -1 + currentIndex;
    if (prev < 0) {
      throw new Error(
        "Contractation out of bounds: No previous enabled step found")
    }
    if (skippedSteps.has(steps[prev].name)) {
      return findPreviousEnabledStepIdx(prev);
    } else {
      return prev;
    }
  }

  const handleGoBack = () => {
    const prevStepIdx = findPreviousEnabledStepIdx(elecContractation.currentStep.index);
    dispatch(electricityContractationActions.resetLeadsErrors());
    dispatch(electricityContractationActions.setLeadCurrentStep(
      prevStepIdx, steps[prevStepIdx].name
    ));
  };

  const findNextEnabledStepIdx = (
    currentIndex: number, 
    stepsToSkip: ElecStepNames[]
  ) => {
    const next = 1 + currentIndex;
    if (
      next < steps.length &&
      stepsToSkip?.includes(steps[next].name)
    ) {
      return findNextEnabledStepIdx(next, stepsToSkip);
    } else {
      return next;
    }
  }

  const getCurrentSkippingSteps = (toSkipSteps: StepChange<ElecStepNames>[] = []) => {
    const currentSkippedSteps = new Set(skippedSteps.values());
    toSkipSteps.forEach(step => 
      step.shouldSkip ? 
        currentSkippedSteps.add(step.name) 
      : currentSkippedSteps.delete(step.name)
    )
    return currentSkippedSteps;
  }

  const handleGoForward = (toSkipSteps?: StepChange<ElecStepNames>[]) => {
    const currentSkippingSteps = getCurrentSkippingSteps(toSkipSteps);
    const followStepIdx = findNextEnabledStepIdx(
      elecContractation.currentStep.index,
      Array.from(currentSkippingSteps)
    );
    if (toSkipSteps) {
      handleChangeSkippingSteps(toSkipSteps);
    }
    if (followStepIdx) {
      dispatch(
        electricityContractationActions.setLeadCurrentStep(
          followStepIdx,
          steps[followStepIdx].name
        )
      );
    }
  };

  const handleChangeSkippingSteps = (toChangeSkippingSteps: StepChange<ElecStepNames>[]) => {

    toChangeSkippingSteps.forEach(stepChange => {
      if (stepChange.shouldSkip) {
        dispatch(electricityContractationActions.setStepData(
          stepChange.name, 
          electricityContractationInitialState["steps"][stepChange.name]))
      }
    })

    const newSkippingSteps = getCurrentSkippingSteps(toChangeSkippingSteps);
    setSkippedSteps(newSkippingSteps);
  }

  let steps: Step<ElecStepNames>[] = [
    {
      name: 'installation',
      label: 'common:text.contractation_about_installation',
      content: <FormInstallation
        type="electricity"
        onSubmit={async (values: StepInstallationFormData, extraData) => {

          const { leadsConsent, changeOwner, ...checkValues } = values;
          const installationValues = { ...checkValues, leadsConsent } as StepInstallationFormData;
          const oldLeadsConsent = elecContractation.leadsConsent;
          if (
            !isEqual(checkValues, elecContractation.steps.installation) ||
            (personFirstFlow && changeOwner !== elecContractation.steps.person.changeOwner) ||
            leadsConsent != elecContractation.leadsConsent
          ) {

            if(checkValues.activeContract != elecContractation.steps.installation.activeContract) {
              setResendLeadType(true);
            }
            if (personFirstFlow && changeOwner !== elecContractation.steps.person.changeOwner) {
              setResendLeadType(true);
            }

            // If leads send to the API
            if (Settings.features.leads &&

              // Has given consent to store leads and receive communications
              (values.leadsConsent ||

                // Edge case in which the user goes back one step 
                // and unchecks leadConsent
                (!values.leadsConsent && oldLeadsConsent)
              )
            ) {
              const elecInstallationFormData = collectFormDataForAPI.collectInstallationFormData(
                values,
                {
                  includeLeadConsent: !personFirstFlow,
                  includeInvoicingAddress: personFirstFlow,
                  ownerContact: personFirstFlow ? elecContractation.steps.person : undefined,
                }
              )

              try {
                const { lead_id: leadId } = await sendTrackedLeadStepData(
                  elecInstallationFormData
                );
                
                await dispatch(
                  electricityContractationActions.setLeadId(leadId)
                );

                if (
                  Settings.features.oidc &&
                  extraData &&
                  Object.keys(extraData).length > 0
                ) {
                  await leadsService.sendLeadStepData(
                    collectFormDataForAPI.collectPreConfigData(
                      extraData,
                      values.cups
                    )
                  );
                }
                
              } catch {
                await dispatch(electricityContractationActions.setLeadsErrors({
                  genericError: true
                }));
                return;
              }

              if (elecContractation.steps.dualGasContract.includeGas) {
                const gasInstallationFormData = elecInstallationFormData;

                // This is needed to find the gas cups lead
                gasInstallationFormData.FormConfirm.cups.name = elecContractation.steps.dualGasContract.cups;

                // Without doing this it wont find the comerOrigin id in the ERP
                // and fail the request.
                gasInstallationFormData.FormConfirm.comerOrigin = elecContractation.steps.dualGasContract.comerOrigin;

                await leadsService.sendGasLeadStepData(
                  gasInstallationFormData
                );
              }
            }

            // Reset errors if there were any
            if (elecContractation.leadErrors.genericError) {
              await dispatch(electricityContractationActions.setLeadsErrors({
                genericError: false
              }));
            }

            // Register values to Redux state
            await dispatch(
              electricityContractationActions.setStepData("installation", installationValues)
            );

            if (personFirstFlow) {
              await dispatch(
                electricityContractationActions.setStepData("person", {
                  ...elecContractation.steps.person,
                  ownerAddress: installationValues.sameInvoiceAddress ? installationValues.address : installationValues.ownerAddress!,
                  sameInvoiceAddress: installationValues.sameInvoiceAddress ?? true,
                  activeContract: installationValues.activeContract,
                  changeOwner: installationValues.activeContract ? changeOwner ?? null : false,
                })
              );
            }

          }

          await dispatch(
            electricityContractationActions.setLeadStepCompleted(
              elecContractation.currentStep.index
            )
          )

          await handleGoForward();

        }}
      />
    },
    {
      name: 'person',
      label: 'common:text.contractation_about_you',
      content: <FormPerson
        type="electricity"
        showInvoicingAddress={!personFirstFlow}
        showLeadConsent={personFirstFlow}
        showChangeOwner={!personFirstFlow}
        onSubmit={async (values: StepPersonFormData) => {
          let skipSteps: StepChange<ElecStepNames>[] | undefined = undefined;

          if (!isEqual(values, elecContractation.steps.person)) {

            // Check if must skip any step
            if (
              Settings?.contractation?.businessHaltContractation &&
              values.company !== elecContractation.steps.person.company
            ) {
              if (values.company) {
                skipSteps = [
                  {name: "contract", shouldSkip: true}, 
                  {name: "dualGasContract", shouldSkip: true}, 
                  {name: "payment", shouldSkip: true}
                ];
              } else {
                skipSteps = [
                  {name: "contract", shouldSkip: false}, 
                  {name: "dualGasContract", shouldSkip: false}, 
                  {name: "payment", shouldSkip: false}
                ];
              }
            }

            const personLeadValues = personFirstFlow ?
              values :
              { ...values, leadsConsent: undefined };

            // If leads send to the API
            if (
              Settings.features.leads &&
              (elecContractation.leadsConsent || values.leadsConsent)
            ) {
              
              if(values.changeOwner != elecContractation.steps.person.changeOwner) {
                setResendLeadType(true);
              }

              const elecPersonFormData = collectFormDataForAPI.collectPersonFormData(
                personLeadValues,
                elecContractation.steps.installation.cups, 
                elecContractation.steps.installation.activeContract,
                { includeSupplyData: !personFirstFlow }
              )

              try {
                const { lead_id: leadId } = await sendTrackedLeadStepData(
                  elecPersonFormData
                );

                await dispatch(
                  electricityContractationActions.setLeadId(leadId)
                );

              } catch {
                await dispatch(electricityContractationActions.setLeadsErrors({
                  genericError: true
                }));

                return;
              }

              if (!personFirstFlow && Settings.contractation?.shouldCheckDNI) {
                const { dni_valid } = await leadsService.checkLeadDNI(elecContractation.leadId)
                if (!dni_valid) {
                  await dispatch(electricityContractationActions.setLeadsErrors({
                    dniInvalid: true
                  }));

                  return;
                }
              };

              if (!personFirstFlow && !!Settings.contractation?.shouldCheckMorosity) {
                const { scoring } = await leadsService.checkLeadScore(elecContractation.leadId);
                if (!scoring) {
                  await dispatch(electricityContractationActions.setLeadsErrors({
                    badScore: true
                  }));

                  return;
                }
              }

              if (!personFirstFlow && elecContractation.steps.dualGasContract) {
                if (elecContractation.steps.dualGasContract.includeGas) {
                  const gasPersonFormData = elecPersonFormData as StepPersonFormDataAPI;
                  gasPersonFormData.FormConfirm.cups.name = elecContractation.steps.dualGasContract.cups;

                  try {
                    await leadsService.sendGasLeadStepData(
                      gasPersonFormData
                    );
                  } catch {
                    await dispatch(electricityContractationActions.setLeadsErrors({
                      genericError: true
                    }));
                    return;
                  }
                }
              }

            }

            // Reset errors if there were any
            if (elecContractation.leadErrors.genericError) {
              await dispatch(electricityContractationActions.setLeadsErrors({
                genericError: false
              }));
            }

            // Register values to Redux state
            await dispatch(
              electricityContractationActions.setStepData("person", personLeadValues)
            );

          }

          // If there is no error
          await dispatch(
            electricityContractationActions.setLeadStepCompleted(
              elecContractation.currentStep.index
            )
          )

          await handleGoForward(skipSteps);

        }}

        onGoBack={handleGoBack}
      />
    },
    {
      name: 'contract',
      label: 'common:text.contractation_about_contract',
      content: <FormContract
        type="electricity"
        onSubmit={async (values) => {

          if (!isEqual(values, elecContractation.steps.contract) || resendLeadType) {

            setResendLeadType(false);

            // If leads send to the API
            if (Settings.features.leads && elecContractation.leadsConsent) {
              try {
                await sendTrackedLeadStepData(
                  collectFormDataForAPI.collectContractFormData(
                    values,
                    elecContractation.steps.installation.cups,
                    elecContractation.steps.installation.activeContract,
                    elecContractation.steps.person.changeOwner
                  )
                );

              } catch {
                await dispatch(electricityContractationActions.setLeadsErrors({
                  genericError: true
                }));

                return;
              }
            }

            // Reset errors if there were any
            if (elecContractation.leadErrors.genericError) {
              await dispatch(electricityContractationActions.setLeadsErrors({
                genericError: false
              }));
            }

            // Register values to Redux state
            await dispatch(
              electricityContractationActions.setStepData("contract", values)
            );
          }

          await dispatch(
            electricityContractationActions.setLeadStepCompleted(
              elecContractation.currentStep.index
            )
          )

          await handleGoForward();

        }}
        onGoBack={handleGoBack}
      />
    },
    {
      name: 'dualGasContract',
      label: 'common:text.contractation_about_gas_contract',
      content: <FormGasContract
        type="electricity"
        onSubmit={async (values) => {

          if (!isEqual(values, elecContractation.steps.dualGasContract)) {

            if (values.includeGas) {

              // If leads send to the API
              if (
                Settings.features.leads && elecContractation.leadsConsent) {

                // Send the first two steps data to a gas contractation
                const gasInstallationFormData = collectFormDataForAPI.collectInstallationFormData({
                  ...elecContractation.steps.installation,

                  // Must do this because otherwise the electricity comer id
                  // is not found in the gas comer available ids and fails 
                  cups: values.cups,
                  activeContract: values.activeContract,
                  comerOrigin: values.comerOrigin,
                  invoice: [] // Avoid sending the electricity invoices at the gas lead
                }, {
                  includeLeadConsent: !personFirstFlow,
                  includeInvoicingAddress: personFirstFlow,
                  ownerContact: personFirstFlow ? elecContractation.steps.person : undefined,
                });

                try {
                  const { lead_id: gasLeadId } = await leadsService.sendGasLeadStepData(
                    gasInstallationFormData
                  );

                  await dispatch(
                    electricityContractationActions.setGasLeadId(gasLeadId)
                  );

                  await leadsService.sendGasLeadStepData(
                    collectFormDataForAPI.collectPersonFormData(
                      {
                        ...elecContractation.steps.person, 
                        changeOwner: values.changeOwner 
                      }, 
                      values.cups, 
                      values.activeContract
                    )
                  );

                  // Send the actual gas step data
                  await leadsService.sendGasLeadStepData(
                    collectFormDataForAPI.collectDualGasContractFormData(
                      values,
                      elecContractation.leadId
                    )
                  );

                } catch {
                  await dispatch(electricityContractationActions.setLeadsErrors({
                    genericError: true
                  }));
                  return;
                }

              }
            }

            // Reset errors if there were any
            if (elecContractation.leadErrors.genericError) {
              await dispatch(electricityContractationActions.setLeadsErrors({
                genericError: false
              }));
            }

            // Register values to Redux state
            await dispatch(
              electricityContractationActions.setStepData("dualGasContract", values)
            );
          }

          await dispatch(
            electricityContractationActions.setLeadStepCompleted(
              elecContractation.currentStep.index
            )
          )

          await handleGoForward();

        }}
        onGoBack={handleGoBack}
      />
    },
    {
      name: 'payment',
      label: 'common:text.contractation_about_payment',
      content: <FormPayment

        type="electricity"
        onSubmit={async (values) => {

          if (!isEqual(values, elecContractation.steps.payment)) {

            // If leads send to the API
            if (Settings.features.leads && elecContractation.leadsConsent) {

              try {
                await sendTrackedLeadStepData(
                  collectFormDataForAPI.collectPaymentFormData(
                    values,
                    elecContractation.steps.installation.cups
                  )
                );

                if (elecContractation.steps.dualGasContract) {
                  if (elecContractation.steps.dualGasContract.includeGas) {
                    // Send the actual gas step data
                    await leadsService.sendGasLeadStepData(
                      collectFormDataForAPI.collectPaymentFormData(
                        values,
                        elecContractation.steps.dualGasContract.cups
                      )
                    );
                  }
                }

              } catch {
                // Reset errors if there were any
                if (elecContractation.leadErrors.genericError) {
                  await dispatch(electricityContractationActions.setLeadsErrors({
                    genericError: true
                  }));
                }
              }

            }

            // Reset errors if there were any
            if (elecContractation.leadErrors.genericError) {
              await dispatch(electricityContractationActions.setLeadsErrors({
                genericError: false
              }));
            }

            // Register values to Redux state
            await dispatch(
              electricityContractationActions.setStepData("payment", values)
            );

          }

          await dispatch(
            electricityContractationActions.setLeadStepCompleted(
              elecContractation.currentStep.index
            )
          )

          await handleGoForward();

        }}

        onGoBack={handleGoBack}
      />
    },
    {
      name: 'confirmation',
      label: 'common:text.contractation_confirmation',
      content: <FormConfirmation
        type="electricity"
        onSubmit={async (values: StepConfirmationFormData) => {

          dispatch(
            electricityContractationActions.setStepData("confirmation", values)
          );

          try {
            let leadId = elecContractation.leadId;

             if (
               // If lead has not been created,
              !(Settings.features.leads && elecContractation.leadsConsent) 
               
               // or some step has dissapeared so data must be synchronized
              || skippedSteps.size > 0
            ) {

              // Send all the steps info
              const { lead_id } = await sendTrackedLeadStepData(
                collectFormDataForAPI.collectElecFormData(
                  elecContractation.steps,
                  { includeInstallationLeadConsent: !personFirstFlow }
                )
              );
              leadId = lead_id;

              if (elecContractation.steps.dualGasContract) {
                if (elecContractation.steps.dualGasContract.includeGas) {
                  await leadsService.sendGasLeadStepData(
                    collectFormDataForAPI.collectDualGasFormData(
                      elecContractation.steps,
                      lead_id,
                      { includeInstallationLeadConsent: !personFirstFlow }
                    )
                  );
                }
              }
            }

            const elecConfirmationData = collectFormDataForAPI.collectConfirmationFormData(
              values,
              elecContractation.steps.installation.cups
            );
            const elecRes = leadId != null
              ? await leadsService.confirmContractation(elecConfirmationData, leadId)
              : await leadsService.confirmContractation(elecConfirmationData);

            if (elecContractation.steps.dualGasContract) {
              if (elecContractation.steps.dualGasContract.includeGas) {
                const gasConfirmationData = collectFormDataForAPI.collectConfirmationFormData(
                  values,
                  elecContractation.steps.dualGasContract.cups
                );
                if (elecContractation.gasLeadId != null) {
                  await leadsService.confirmGasContractation(
                    gasConfirmationData,
                    elecContractation.gasLeadId
                  );
                } else {
                  await leadsService.confirmGasContractation(gasConfirmationData);
                }
              }
            }

            await dispatch(
              electricityContractationActions.receiveConfirmNewContractResponse(
                elecRes
              )
            );

            await dispatch(
              electricityContractationActions.setLeadStepCompleted(
                elecContractation.currentStep.index
              )
            )

            await handleGoForward();


          } catch (err) {

            dispatch(
              electricityContractationActions.receiveConfirmNewContractResponse(
                {
                  result: {
                    error: true,
                    render: {
                      format: 'markdown',
                      body: 'common:text.contractation_connectivity_error'
                    }
                  },
                  status: 'ERROR'
                }
              )
            );
          }

        }}
        onGoBack={handleGoBack}
      />
    },
    {
      name: 'finish',
      label: 'common:text.contractation_confirmed',
      content: <Finish
        type="electricity"
        onGoBack={handleGoBack}
      />
    }
  ];

  if (personFirstFlow) {
    const personStepIdx = steps.findIndex(step => step.name === "person");
    if (personStepIdx > 0) {
      const [personStep] = steps.splice(personStepIdx, 1);
      steps.unshift(personStep);
    }
  }

  const removeStep = (stepName: ElecStepNames) => {
    steps = steps.filter(step => step.name !== stepName);
  }

  if (!dualContractation) {
    removeStep('dualGasContract');
  }

  return {
    steps,
    handleGoBack
  }

}

export default useSteps;
